import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PenLine, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DossierItemPicker, DossierTextEditor } from "@/components/DossierItems";

export default function DossierEditDialog({ dossier, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [tlOn, setTlOn] = useState(false);
  const [checked, setChecked] = useState({});
  const [texts, setTexts] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dossier) { return; }
    const cl = dossier.cover_letter || {};
    setForm({ title: dossier.title || "", description: dossier.description || "", clInclude: !!cl.include, clSubject: cl.subject || "", clBody: cl.body || "" });
    setTexts(dossier.item_texts || {});
    api.get(`/dossiers/${dossier.id}/items`).then((r) => {
      const its = (r.data.items || []).map((e) => ({ ...e, title: e.orig_title, description: e.orig_description }));
      setTlOn(!!r.data.include); setItems(its);
      setChecked(Object.fromEntries(r.data.items.map((e) => [e.id, e.selected])));
    }).catch(() => setItems([]));
  }, [dossier]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titel mag niet leeg zijn"); return; }
    setBusy(true);
    const ids = items.filter((e) => checked[e.id]).map((e) => e.id);
    try {
      await api.put(`/dossiers/${dossier.id}`, {
        title: form.title, description: form.description,
        cover_letter: { include: form.clInclude, subject: form.clSubject, body: form.clBody },
        item_ids: tlOn ? ids : null,
        item_texts: Object.fromEntries(Object.entries(texts).filter(([k]) => ids.includes(k))),
      });
      toast.success(`Dossier ${dossier.number} bijgewerkt — PDF opnieuw opgemaakt`);
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  const sel = items.filter((e) => checked[e.id]);
  const f = (k) => (ev) => setForm((s) => ({ ...s, [k]: ev.target.value }));

  return (
    <Dialog open={!!dossier} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dossier-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="h-5 w-5 text-[#C9A227]" /> Dossier {dossier?.number} bewerken</DialogTitle>
          <DialogDescription>Pas de tekst en selectie aan. Bij opslaan wordt de PDF opnieuw opgemaakt.</DialogDescription>
        </DialogHeader>
        {form && (
          <Tabs defaultValue="tekst" className="min-w-0">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="tekst" data-testid="dossier-edit-tab-text">Tekst</TabsTrigger>
              <TabsTrigger value="selectie" disabled={!tlOn} data-testid="dossier-edit-tab-select">Selectie ({sel.length})</TabsTrigger>
              <TabsTrigger value="tijdslijn" disabled={!tlOn} data-testid="dossier-edit-tab-items">Tijdslijn-tekst</TabsTrigger>
            </TabsList>
            <TabsContent value="tekst" className="space-y-3 mt-4">
              <div><Label>Titel</Label><Input value={form.title} onChange={f("title")} data-testid="dossier-edit-title" /></div>
              <div><Label>Toelichting op het voorblad</Label><Textarea value={form.description} onChange={f("description")} rows={3} data-testid="dossier-edit-description" /></div>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.clInclude} onCheckedChange={() => setForm((s) => ({ ...s, clInclude: !s.clInclude }))} data-testid="dossier-edit-cl-include" /> Begeleidende brief opnemen</label>
              {form.clInclude && (
                <div className="space-y-2">
                  <Input value={form.clSubject} onChange={f("clSubject")} placeholder="Onderwerp" data-testid="dossier-edit-cl-subject" />
                  <Textarea value={form.clBody} onChange={f("clBody")} rows={7} data-testid="dossier-edit-cl-body" />
                </div>
              )}
            </TabsContent>
            <TabsContent value="selectie" className="mt-4"><DossierItemPicker items={items} checked={checked} setChecked={setChecked} /></TabsContent>
            <TabsContent value="tijdslijn" className="mt-4"><DossierTextEditor items={sel} texts={texts} setTexts={setTexts} /></TabsContent>
          </Tabs>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={save} disabled={busy || !form} data-testid="dossier-edit-save"><RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} /> {busy ? "PDF opmaken…" : "Opslaan & PDF opnieuw opmaken"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
